import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ThemeSwitcher } from './ThemeSwitcher'
import { useThemeTokens } from '../themes'
import { hasPermission } from '../data/users'
import type { LayoutProps } from '../types/index.js'
import type { UserRole } from '../data/users'

const Layout: React.FC<LayoutProps> = ({ children, onLogout, user }) => {
  const theme = useThemeTokens()
  const navigate = useNavigate()
  const location = useLocation()
  
  const getCurrentPage = () => {
    if (location.pathname.startsWith('/orders')) return 'orders'
    if (location.pathname === '/dashboard') return 'dashboard'
    if (location.pathname === '/timesheet' && !location.pathname.includes('reports')) return 'timesheet'
    if (location.pathname === '/timesheet-reports') return 'reports'
    if (location.pathname === '/essco-products') return 'essco'
    if (location.pathname === '/components') return 'components'
    return 'dashboard'
  }
  
  const currentPage = getCurrentPage()
  
  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
      <header style={{
        backgroundColor: theme.colors.surface,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
        boxShadow: theme.shadows.sm
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <h1 style={{ 
              color: theme.colors.text, 
              margin: '0',
              fontSize: theme.typography.fontSize.xl,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily
            }}>
              Zeigler Environmental - Internal
            </h1>
            
            {/* Navigation */}
            <nav style={{ display: 'flex', gap: '1rem' }}>
              {user && hasPermission(user.role as UserRole, 'dashboard') && (
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    backgroundColor: currentPage === 'dashboard' ? theme.colors.primary : 'transparent',
                    color: currentPage === 'dashboard' ? 'white' : theme.colors.text,
                    border: `1px solid ${theme.colors.primary}`,
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  Dashboard
                </button>
              )}
              {user && hasPermission(user.role as UserRole, 'orders') && (
                <button
                  onClick={() => navigate('/orders')}
                  style={{
                    backgroundColor: currentPage === 'orders' ? theme.colors.primary : 'transparent',
                    color: currentPage === 'orders' ? 'white' : theme.colors.text,
                    border: `1px solid ${theme.colors.primary}`,
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  Orders
                </button>
              )}
              {user && hasPermission(user.role as UserRole, 'timesheets') && (
                <button
                  onClick={() => navigate('/timesheet')}
                  style={{
                    backgroundColor: currentPage === 'timesheet' ? theme.colors.primary : 'transparent',
                    color: currentPage === 'timesheet' ? 'white' : theme.colors.text,
                    border: `1px solid ${theme.colors.primary}`,
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  Timesheet
                </button>
              )}
              {user && hasPermission(user.role as UserRole, 'reports') && (
                <button
                  onClick={() => navigate('/timesheet-reports')}
                  style={{
                    backgroundColor: currentPage === 'reports' ? theme.colors.primary : 'transparent',
                    color: currentPage === 'reports' ? 'white' : theme.colors.text,
                    border: `1px solid ${theme.colors.primary}`,
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  Reports
                </button>
              )}
              {user && hasPermission(user.role as UserRole, 'orders') && (
                <button
                  onClick={() => navigate('/essco-products')}
                  style={{
                    backgroundColor: currentPage === 'essco' ? theme.colors.primary : 'transparent',
                    color: currentPage === 'essco' ? 'white' : theme.colors.text,
                    border: `1px solid ${theme.colors.primary}`,
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  ESSCO Products
                </button>
              )}
              {user && hasPermission(user.role as UserRole, 'settings') && (
                <button
                  onClick={() => navigate('/components')}
                  style={{
                    backgroundColor: currentPage === 'components' ? theme.colors.primary : 'transparent',
                    color: currentPage === 'components' ? 'white' : theme.colors.text,
                    border: `1px solid ${theme.colors.primary}`,
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  Components
                </button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
            {user && (
              <span style={{ 
                color: theme.colors.text, 
                fontSize: theme.typography.fontSize.sm,
                fontFamily: theme.typography.fontFamily
              }}>
                Welcome, {user.username}
              </span>
            )}
            <ThemeSwitcher />
            <button
              data-testid="logout-button"
              onClick={onLogout}
              style={{
                backgroundColor: theme.colors.error,
                color: 'white',
                border: 'none',
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                borderRadius: theme.borderRadius.sm,
                cursor: 'pointer',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.bold,
                fontFamily: theme.typography.fontFamily
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      <main>
        {children}
      </main>
    </div>
  )
}

export default Layout 